export type TOptions = {
  uri: string;
  method: string;
};

type GetCausedBy = (options: any) => TOptions;

const getCausedBy: GetCausedBy = function <Options extends TOptions>(options?: Options) {
  const { uri = '', method = 'GET' } = options || {};

  return { uri, method };
};

/**
 * Request failed due to technical reasons of network or API server
 */
class ApiCallError extends Error {
  code: string | number;
  causedBy: TOptions;

  // the following properties are added to avoid the ambiguity of the code and message parameters.
  /**
   * HTTP status code
   */
  statusCode?: number;
  /**
   * error code from JSON response
   */
  errorCode?: string;
  /**
   * error message from JSON response
   */
  errorMessage?: string;

  #responseBody: string | object | undefined;

  /**
   * @param [apiCallOptions] extra api call information to be logged
   */
  constructor(code: string | number, message: string, apiCallOptions?: Partial<TOptions>) {
    super(message);
    this.code = code;
    this.name = 'ApiCallError';

    this.causedBy = getCausedBy(apiCallOptions);
  }

  setResponseBody(body: string | object) {
    this.#responseBody = body;
  }

  getResponseBody(): string | object | undefined {
    return this.#responseBody;
  }
}

/**
 * Request failed due to the wrong data provided by client
 */
class ClientError extends Error {
  causedBy: TOptions;
  code: string | number;
  details?: object;

  // the following properties are added to avoid the ambiguity of the code and message parameters.
  /**
   * HTTP status code
   */
  statusCode?: number;
  /**
   * error code from JSON response
   */
  errorCode?: string;
  /**
   * error message from JSON response
   */
  errorMessage?: string;

  /**
   * @param [details] Error code per field
   * @param [apiCallOptions] extra api call information to be logged
   */
  constructor(code: string | number, message: string, details?: object, apiCallOptions?: Partial<TOptions>) {
    super(message);
    this.code = code;
    this.name = 'ClientError';
    this.details = details;

    this.causedBy = getCausedBy(apiCallOptions);
  }
}

/**
 * @param [code] if set, then code should also match
 */
function isError(e: any, name: string, code?: string | string[]): boolean {
  const codes = code && !Array.isArray(code) ? [code] : code || [];

  return e.name === name && (!code || codes.includes(String(e.code)));
}

/**
 * @param [code] if set, then code should also match
 */
function isClientError(e: any, code?: string | string[]): e is ClientError {
  return e instanceof Error && isError(e, 'ClientError', code);
}

/**
 * @param [code] if set, then code should also match
 */
function isApiCallError(e: any, code?: string | string[]): e is ApiCallError {
  return e instanceof Error && isError(e, 'ApiCallError', code);
}

export { ApiCallError, ClientError, isClientError, isApiCallError };
